import * as amplitude from '@amplitude/analytics-browser'

export type ContentCta = 'preset' | 'batch' | 'more_images'

export interface ContentTouch {
  page: string
  lang?: string
  cta?: ContentCta
  at: number
}

export interface ContentAttributionState {
  first?: ContentTouch
  last?: ContentTouch
}

export const CONTENT_ATTRIBUTION_STORAGE_KEY = 'content_attribution_v1'
export const CONTENT_ATTRIBUTION_TTL_MS = 1000 * 60 * 60 * 24 * 30 // 30 days

const PAGE_PATTERN = /^[a-z0-9-]+(\/[a-z0-9-]+)*$/
const PAGE_MAX_LENGTH = 100
const LANG_PATTERN = /^[a-z]{2}(-[A-Za-z]{2,4})?$/
const CTA_VALUES: readonly ContentCta[] = ['preset', 'batch', 'more_images']

// Touch captured in the current page visit. Kept in memory so it is available
// even when it was not (or could not be) persisted to localStorage.
let currentVisit: ContentTouch | null = null

function isBrowser() {
  return typeof window !== 'undefined'
}

export function contentAttributionRequiresConsent(): boolean {
  return process.env.REACT_APP_CONTENT_ATTRIBUTION_REQUIRES_CONSENT === '1'
}

export function parseContentParams(search: string): Omit<ContentTouch, 'at'> | null {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return null
  }
  const page = params.get('bz_page')
  if (!page || page.length > PAGE_MAX_LENGTH || !PAGE_PATTERN.test(page)) return null

  const result: Omit<ContentTouch, 'at'> = { page }
  const lang = params.get('bz_lang')
  if (lang && LANG_PATTERN.test(lang)) result.lang = lang
  const cta = params.get('bz_cta')
  if (cta && (CTA_VALUES as readonly string[]).includes(cta)) result.cta = cta as ContentCta
  return result
}

function sanitizeTouch(raw: unknown, now: number): ContentTouch | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const t = raw as Record<string, unknown>
  if (typeof t.page !== 'string' || t.page.length > PAGE_MAX_LENGTH || !PAGE_PATTERN.test(t.page)) {
    return undefined
  }
  if (typeof t.at !== 'number' || !Number.isFinite(t.at)) return undefined
  if (now - t.at >= CONTENT_ATTRIBUTION_TTL_MS) return undefined
  const touch: ContentTouch = { page: t.page, at: t.at }
  if (typeof t.lang === 'string' && LANG_PATTERN.test(t.lang)) touch.lang = t.lang
  if (typeof t.cta === 'string' && (CTA_VALUES as readonly string[]).includes(t.cta)) {
    touch.cta = t.cta as ContentCta
  }
  return touch
}

function readStoredState(now: number): ContentAttributionState {
  if (!isBrowser()) return {}
  try {
    const raw = window.localStorage.getItem(CONTENT_ATTRIBUTION_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as ContentAttributionState
    const state: ContentAttributionState = {}
    const first = sanitizeTouch(parsed?.first, now)
    const last = sanitizeTouch(parsed?.last, now)
    if (first) state.first = first
    if (last) state.last = last
    return state
  } catch {
    return {}
  }
}

export function shouldPersistContentAttribution(
  requiresConsent: boolean,
  marketingKnownGranted: boolean
): boolean {
  return !requiresConsent || marketingKnownGranted
}

export function clearStoredContentAttribution(): void {
  if (!isBrowser()) return
  try {
    window.localStorage.removeItem(CONTENT_ATTRIBUTION_STORAGE_KEY)
  } catch {
    // no-op
  }
}

function writeState(state: ContentAttributionState): void {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(CONTENT_ATTRIBUTION_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // no-op
  }
}

function getCurrentVisit(now: number): ContentTouch | null {
  currentVisit = sanitizeTouch(currentVisit, now) ?? null
  return currentVisit
}

export function captureContentAttributionFromSearch(
  search: string,
  opts: { canPersist: boolean; now?: number }
): ContentAttributionState {
  const now = opts.now ?? Date.now()
  const parsed = parseContentParams(search)
  if (!parsed) return getContentAttribution(now)

  const touch: ContentTouch = { ...parsed, at: now }
  currentVisit = touch
  const stored = readStoredState(now)
  const state: ContentAttributionState = { first: stored.first ?? touch, last: touch }
  if (opts.canPersist) writeState(state)
  return state
}

export function persistCurrentVisit(now: number = Date.now()): void {
  const visit = getCurrentVisit(now)
  if (!visit) return
  const stored = readStoredState(now)
  writeState({ first: stored.first ?? visit, last: visit })
}

export function getContentAttribution(now: number = Date.now()): ContentAttributionState {
  const stored = readStoredState(now)
  const visit = getCurrentVisit(now)
  if (!visit) return stored
  return { first: stored.first ?? visit, last: visit }
}

export function getCurrentVisitContentPage(now: number = Date.now()): string | undefined {
  return getCurrentVisit(now)?.page
}

/** Call after the analytics opt-out state reflects the new consent selection. */
export function updateContentAttributionConsent(marketingGranted: boolean): void {
  if (shouldPersistContentAttribution(contentAttributionRequiresConsent(), marketingGranted)) {
    persistCurrentVisit()
  } else {
    clearStoredContentAttribution()
  }
  applyContentUserProperties(getContentAttribution())
}

export function withContentAttribution<T extends Record<string, unknown>>(
  payload: T
): T & { 'Content First Page'?: string; 'Content Last Page'?: string } {
  const { first, last } = getContentAttribution()
  if (!first && !last) return payload
  const extra: { 'Content First Page'?: string; 'Content Last Page'?: string } = {}
  if (first) extra['Content First Page'] = first.page
  if (last) extra['Content Last Page'] = last.page
  return { ...payload, ...extra }
}

export function applyContentUserProperties(state: ContentAttributionState): void {
  const { first, last } = state
  if (!last) return
  try {
    const identifyEvent = new amplitude.Identify()
    if (first) identifyEvent.setOnce('Content First Page', first.page)
    identifyEvent.set('Content Last Page', last.page)
    if (last.lang) identifyEvent.set('Content Language', last.lang)
    else identifyEvent.unset('Content Language')
    if (last.cta) identifyEvent.set('Content CTA', last.cta)
    else identifyEvent.unset('Content CTA')
    amplitude.identify(identifyEvent)
  } catch {
    // analytics must never break the app
  }
}
